import React from 'react';
import { Flex, Text, Tooltip } from '@chakra-ui/react';
import { KeyMetricsNumberFormatter } from 'src/utils/TickFormatters';
import { useCurrency } from 'src/hooks/useCurrency';
import { getCurrencyAffixes } from 'src/utils/currency';
import { TrendIndicator } from '../../atoms/TrendIndicator';
import { DataError } from '../DataStatusWrapper/DataError';
import { DataLoading } from '../DataStatusWrapper/DataLoading';

type KeyMetricCardProps = {
  title: string;
  icon: React.ReactNode;
  value: number;
  // Monetary values get the customer currency symbol (12.5M zł / $12.5M)
  isEuro?: boolean;
  // Omit both trend props to render the value without an indicator
  trend?: 'up' | 'down';
  trendValue?: string;
  trendTooltip?: string;
  // Absolute comparison value shown as "vs N" under the main value
  baselineValue?: number;
  isLoading?: boolean;
  isError?: boolean;
};

const defaultTrendTooltip =
  'Shows how much the latest value has increased or decreased compared to the first value within the selected date range.';

export const KeyMetricCard: React.FC<KeyMetricCardProps> = ({
  title,
  icon,
  value,
  isEuro = false,
  trend,
  trendValue,
  trendTooltip = defaultTrendTooltip,
  baselineValue,
  isLoading = false,
  isError = false,
}) => {
  const { code } = useCurrency();
  const { prefix, suffix } = isEuro
    ? getCurrencyAffixes(code)
    : { prefix: '', suffix: '' };

  if (isError || isLoading) {
    return (
      <Flex
        p={{ base: 4, sm: 4, md: 4 }}
        align="center"
        justifyContent="center"
        minH="90px">
        {isError ? <DataError /> : <DataLoading />}
      </Flex>
    );
  }

  const [formattedValue, magnitude] = KeyMetricsNumberFormatter(value);

  return (
    <Flex
      p={{ base: 4, sm: 4, md: 4 }}
      align="center"
      justifyContent="space-between">
      <Flex direction="column" gap={{ base: 1, md: 2, lg: 2 }}>
        <Text
          fontSize={{ base: 'xs', md: 'sm', lg: 'sm' }}
          fontWeight="semibold"
          color="content.label"
          fontFamily="marketingPage">
          {title}
        </Text>
        <Flex alignItems="center" gap={{ base: 1, sm: 1, md: 2, lg: 2 }}>
          <Text
            fontSize={{ base: 'md', md: 'lg', lg: 'xl' }}
            fontWeight="bold"
            color="content.title"
            fontFamily="marketingPage">
            {`${prefix}${formattedValue}${magnitude}${suffix}`}
          </Text>
          {trend && trendValue && (
            <Tooltip label={trendTooltip}>
              <Flex alignItems="center" gap={1} cursor="pointer">
                <TrendIndicator trend={trend} />
                <Text
                  color={trend === 'up' ? 'icon.green' : 'icon.red'}
                  fontSize="sm"
                  fontWeight="semibold">
                  {trendValue}
                </Text>
              </Flex>
            </Tooltip>
          )}
        </Flex>
        {baselineValue !== undefined && (
          <Text fontSize="xs" color="content.label" fontFamily="marketingPage">
            {`vs ${prefix}${KeyMetricsNumberFormatter(baselineValue).join('')}${suffix}`}
          </Text>
        )}
      </Flex>
      <Flex
        bg="surface.sidebarMuted"
        p={2}
        alignItems="center"
        height={{ base: '16px', md: '24px', lg: '40px', xl: '50px' }}
        borderRadius="8px"
        width={{ base: '16px', md: '24px', lg: '40px', xl: '50px' }}
        justifyContent="center">
        {icon}
      </Flex>
    </Flex>
  );
};
